'use client';

import type { KeyboardEvent } from 'react';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Icon } from '@/components/entities/Sprite';
import { useMarketFiatCurrency } from '@/hooks/useMarketFiatCurrency';
import { Link } from '@/i18n/routing';
import missionXpBadge from '@/public/assets/image/gamification/missions/mission-xp-badge@3x.png';
import { cn } from '@/utils/cn';
import { formatCentsToCurrencyNumber, formatCompactNumber, getFiatCurrencyPrefix } from '@/utils/currency.utils';
import { calculateProgressPercent } from '@/utils/progress.utils';
import { GmProgressBar } from '../GmProgressBar';
import { getMissionTitleParts } from './utils/missionTitle.utils';

interface Props extends UserQuestDto {
  onAction?: VoidFunction;
  href?: string;
}

const MIN_FILL_VIEW_PROGRESS_BAR = 5;
/** Placeholder in a mission name replaced with the player's market currency symbol (`$` / `C$`). */
const CURRENCY_PLACEHOLDER = '{{CUR}}';

export const GmMissionCard = ({ onAction, href, ...mission }: Props) => {
  const t = useTranslations('gamification.mission-card');
  const { currency } = useMarketFiatCurrency();
  const isSumTotalProgress = mission.quest.progress.type === 'sum';
  const done = isSumTotalProgress
    ? formatCentsToCurrencyNumber(mission.progress)
    : mission.progress ?? 0;
  const total = (isSumTotalProgress ? formatCentsToCurrencyNumber : (val: number) => val)(
    mission.quest.progress.target ?? 0,
  );
  const percentageProgress = calculateProgressPercent(done, total);
  const title = mission.quest.name.replaceAll(CURRENCY_PLACEHOLDER, getFiatCurrencyPrefix(currency));
  const titleParts = getMissionTitleParts(title);
  const xp = mission.quest.xpAmount ?? 0;
  const formattedXp = formatCompactNumber(xp, { minUnit: 'thousand' });

  const isInteractive = !!onAction || !!href;
  const missionsProgress = Math.min(percentageProgress, 100);
  const missionCompleted = !!mission.completedAt;
  const missionPercentProgress = Math.max(missionsProgress, done ? MIN_FILL_VIEW_PROGRESS_BAR : 0);

  const handleMissionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onAction?.();
    }
  };

  const className = cn(
    'rounded-2xl glass-stroke-border box-border shadow-card gm-mission-gradient w-full',
    'flex min-h-23 items-center justify-between gap-2 px-4 py-4',
    isInteractive && 'cursor-pointer',
    missionCompleted && 'opacity-50',
  );

  const content = (
    <>
      <div className="flex min-w-0 grow flex-col gap-3">
        <p className="text-sm text-start font-normal leading-5 text-light">
          {titleParts.map(({ text, highlighted }, index) => highlighted
            ? <span key={index} className="gm-text-golden-3 font-semibold!">{text}</span>
            : text)}
        </p>

        <div className="relative">
          <GmProgressBar
            showLightEffect={false}
            percent={missionPercentProgress}
            className="**:data-[slot=progressBarTrack]:z-10"
          />

          <p className="text-c300 font-semibold leading-[0.813rem] absolute lg:mr-px right-2 top-0 text-[0.688rem]">
            {`${done}/${total}`}
          </p>
        </div>
      </div>

      <div className="relative flex size-15 shrink-0 items-center justify-center">
        <Image src={missionXpBadge} alt="Mission XP Badge" aria-hidden className="pointer-events-none absolute inset-0 size-full" />
        <div className="relative flex max-w-12 font-pp-right-grotesk flex-col items-center font-semibold">
          <span className="gm-mission-xp-bg whitespace-nowrap text-[1.375rem] font-extrabold leading-[100%]">
            {formattedXp}
          </span>
          <span className="gm-mission-xp-bg text-sm font-black leading-2.5">{t('xp')}</span>
        </div>
      </div>

      {isInteractive && <Icon name="arrow-up" className="absolute right-2 top-2 size-4 rotate-45 text-light" />}
    </>
  );

  if (href) {
    return (
      <Link href={href} onClick={onAction} className={className}>
        {content}
      </Link>
    );
  }

  if (onAction) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onAction}
        onKeyDown={handleMissionKeyDown}
        className={className}
      >
        {content}
      </div>
    );
  }

  return <div className={className}>{content}</div>;
};
